import React, { useCallback, useEffect, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, HardDrive, Loader2, Unplug, RefreshCw } from "lucide-react";

const OWNER_GOOGLE = "rickkinky@gmail.com";

const STATUS_LABEL = {
  connected: { text: "Connected", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
  reauthorization_required: { text: "Needs reconnection", cls: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  not_connected: { text: "Not connected", cls: "bg-white/5 text-muted-foreground border-white/10" },
  disconnected: { text: "Not connected", cls: "bg-white/5 text-muted-foreground border-white/10" },
};

// Owner-only Settings: Google Drive private original/derivative archive connection (master owner only — ADMIN_EMAILS admins never see this).
export default function OwnerSettings() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_master_owner) return <Navigate to="/browse" replace />;
  return <SettingsBody />;
}

const SettingsBody = () => {
  const [params, setParams] = useSearchParams();
  const [drive, setDrive] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get("/owner/gdrive/status").then(({ data }) => setDrive(data)).catch(() => setDrive({ status: "not_connected", configured: false })), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const g = params.get("gdrive");
    if (g === "connected") toast.success("Google Drive connected");
    else if (g === "error") toast.error(`Google Drive connection failed (${params.get("reason") || "unknown"})`);
    if (g) { params.delete("gdrive"); params.delete("reason"); setParams(params, { replace: true }); }
  }, [params, setParams]);

  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/owner/gdrive/connect");
      window.location.assign(data.auth_url);           // Google consent (drive.file · offline · consent) → backend callback → back here
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start Google Drive connection");
      setBusy(false);
    }
  };
  const disconnect = async () => {
    if (!window.confirm("Disconnect Google Drive? Archived files stay in your Drive; playback is not affected. New backups stop until you reconnect.")) return;
    setBusy(true);
    try { await api.post("/owner/gdrive/disconnect"); toast.success("Google Drive disconnected"); await load(); }
    catch (e) { toast.error("Disconnect failed"); }
    finally { setBusy(false); }
  };

  const st = drive?.status || "not_connected";
  const label = STATUS_LABEL[st] || STATUS_LABEL.not_connected;
  const connected = st === "connected";
  const needsReauth = st === "reauthorization_required";

  return (
    <div className="min-h-screen relative z-10" data-testid="owner-settings">
      <Navbar />
      <main className="px-4 pt-5 pb-24 space-y-6 max-w-lg md:max-w-2xl lg:max-w-3xl lg:px-8 lg:pt-8 mx-auto">
        <Link to="/owner" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="owner-settings-back"><ArrowLeft className="w-4 h-4" /> Owner</Link>
        <h1 className="text-2xl font-semibold tracking-tight">Settings</h1>

        <section className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 space-y-4" data-testid="gdrive-settings-section">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2">
              <HardDrive className="w-5 h-5 text-primary" />
              <div>
                <h2 className="text-base font-semibold">Google Drive Backup</h2>
                <p className="text-xs text-muted-foreground">Private archive for your originals and processed videos. Nothing in Drive is ever shared publicly.</p>
              </div>
            </div>
            <span className={`shrink-0 rounded-full border px-2.5 py-0.5 text-[11px] font-medium ${label.cls}`} data-testid="gdrive-status-chip">{label.text}</span>
          </div>

          {drive === null ? (
            <div className="text-sm text-muted-foreground inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Checking…</div>
          ) : !drive.configured ? (
            <div className="text-sm text-amber-300" data-testid="gdrive-not-configured">Google Drive OAuth is not configured on this server yet.</div>
          ) : (
            <>
              {(connected || needsReauth) && (
                <div className="text-sm" data-testid="gdrive-account">Connected to: <span className="font-medium">{drive.google_account_email || OWNER_GOOGLE}</span>
                  {drive.last_error && <div className="text-xs text-amber-300 mt-1" data-testid="gdrive-last-error">Last error: {drive.last_error}</div>}
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                {!connected && (
                  <button type="button" onClick={connect} disabled={busy} data-testid="gdrive-connect-btn"
                    className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-60 active:scale-[0.98] transition-transform">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <HardDrive className="w-4 h-4" />}
                    {needsReauth ? "Reconnect Google Drive" : `Connect Google Drive (${OWNER_GOOGLE})`}
                  </button>
                )}
                {connected && (
                  <>
                    <button type="button" onClick={connect} disabled={busy} data-testid="gdrive-reconnect-btn" className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm hover:bg-white/5"><RefreshCw className="w-4 h-4" /> Reconnect</button>
                    <button type="button" onClick={disconnect} disabled={busy} data-testid="gdrive-disconnect-btn" className="inline-flex items-center gap-2 rounded-full border border-red-500/30 px-4 py-2 text-sm text-red-300 hover:bg-red-500/10"><Unplug className="w-4 h-4" /> Disconnect</button>
                  </>
                )}
              </div>
              <p className="text-[11px] text-muted-foreground">Scope: Drive files created by BareFootage only (drive.file). Your refresh credential is stored encrypted on the server and never shown.</p>
            </>
          )}
        </section>
      </main>
    </div>
  );
};
